/* eslint-disable no-param-reassign */
/* eslint-disable react/jsx-indent */
/* eslint-disable react-hooks/rules-of-hooks */
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Grid,
  Stepper,
  Step,
  StepLabel,
  CircularProgress,
  Typography,
} from '@mui/material';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery } from '@apollo/client';
import { Form as FinalForm } from 'react-final-form';
import { useTranslation } from 'react-i18next';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { Helmet } from 'react-helmet-async';
import LoadingButton from '@mui/lab/LoadingButton';
import { useConfirm } from 'material-ui-confirm';
import { getDetailsGql, updateCheckoutGql, createPaymentIntentGql } from './gql';
import ChooseTicketCategory from './components/ChooseTicketCategory';
import EventDetails from './components/EventDetails';
import AttendeeDetails from './components/AttendeeDetails';
import InvoiceAddress from './components/InvoiceAddress';
import { formatCheckoutDataToState, formatStateToCheckout } from './helper';
import Review from './components/Review';
import { LayoutContext } from '../../stores/LayoutStore';
import Tracking from '../../components/Tracking';

function CheckoutPage() {
  const params = useParams();
  const [activeStep, setActiveStep] = useState(0);
  const [stripe, setStripe] = useState(null);
  const [stripeLoading, setStripeLoading] = useState(false);
  const [stripeElements, setStripeElements] = useState(null);
  const [nextButtonActive, setNextButtonActive] = useState(false);
  const formRef = useRef();
  const contentRef = useRef();
  const { t } = useTranslation('checkout');
  const confirm = useConfirm();
  const [searchParams, setSearchParams] = useSearchParams();
  const layoutContext = useContext(LayoutContext);
  const navigate = useNavigate();
  const location = useLocation();

  const { data, refetch, loading } = useQuery(getDetailsGql, {
    variables: {
      slug: params.slug,
    },
  });

  const [updateCheckout, { loading: loadingUpdateCheckout }] = useMutation(updateCheckoutGql, {
    variables: { slug: params.slug },
  });

  const [createPaymentIntent, { loading: loadingPaymentIntent }] = useMutation(createPaymentIntentGql, {
    variables: { slug: params.slug },
  });

  const details = useMemo(() => {
    if (data?.getDetails) {
      return {
        ...data?.getDetails,
        products: data.getDetails.products.map((orgProduct) => {
          const product = { ...orgProduct };
          const avail = data.getAvailabilities.availabilities.find(({ productId }) => productId === product.id);
          product.isAvailable = avail?.isAvailable;
          product.maxQuantity = avail?.maxQuantity;
          return product;
        }),
      };
    }
    return {};
  }, [data?.getDetails, data?.getAvailabilities?.availabilities]);

  const stripePublishableKey = details?.event?.stripeSettings?.publishableKey;
  const stripePromise = useMemo(
    () => (stripePublishableKey ? loadStripe(stripePublishableKey) : null),
    [stripePublishableKey],
  );
  const pageTitle = details?.event?.frontendSettings?.pageTitle;
  const faviconUrl = details?.event?.frontendSettings?.assets?.favicon;

  const initValues = useMemo(() => formatCheckoutDataToState(data), [data?.getDetails]);

  const steps = (params.slug === 'kongress-bw-2025-demo')
    ? [t('steps.chooseTicket'), t('steps.enterAttendee'), 'Foren', t('steps.invoiceAddress'), t('steps.summary')]
    : [t('steps.chooseTicket'), t('steps.enterAttendee'), t('steps.invoiceAddress'), t('steps.summary')];

  const getStepFromLocation = () => {
    const stepMatch = location.hash.match(/step-(\d+)/);
    return stepMatch ? Math.min(parseInt(stepMatch[1], 10), steps.length - 1) : 0;
  };

  useEffect(() => {
    setActiveStep(getStepFromLocation());
  }, [location.hash]);

  const handleBack = () => {
    if (formRef.current.getState().invalid) formRef.current.reset(formatCheckoutDataToState(data));
    if (activeStep > 0) navigate(`/${params.slug}/checkout#step-${activeStep - 1}`);
  };

  useEffect(() => {
    layoutContext.setSlug(params.slug);
  }, [params.slug]);

  useEffect(() => {
    if (loading || !data?.getCheckout) return;
    if (activeStep > 0 && data.getCheckout.lineItems?.length === 0) {
      navigate(`/${params.slug}/checkout#step-0`);
    }
  }, [data?.getCheckout, activeStep, loading, navigate, params.slug]);

  useEffect(() => {
    if (searchParams.get('error') !== 'failedPayment' || loading) return;
    setSearchParams((params) => {
      params.delete('error');
      return params;
    });
    navigate(`/${params.slug}/checkout#step-${steps.length - 1}`, { replace: true });
    contentRef.current?.scrollIntoView();
    confirm({
      title: t('paymentErrors.headline'),
      description: t('paymentErrors.general'),
      hideCancelButton: true,
    });
  }, [searchParams, loading]);

  const onSubmit = async (values) => {
    if (activeStep === 3) {
      let confirmationToken;
      if (data?.getCheckout?.total > 0) {
        setStripeLoading(true);
        if (!stripe || !stripeElements) {
          setStripeLoading(false);
          await confirm({
            title: t('paymentErrors.headline'),
            description: t('paymentErrors.general'),
            hideCancelButton: true,
          });
          return;
        }
        const { error: submitError, selectedPaymentMethod } = await stripeElements.submit();
        if (selectedPaymentMethod.startsWith('cpmt_')) {
          try {
            await createPaymentIntent({
              variables: { slug: params.slug, input: { selectedPaymentMethod } },
            });
            window.location.href = `${window.location.origin}/payment-complete/${params.slug}/stripe`;
            return;
          } catch (error) {
            const checkoutErrorCode = error?.networkError?.result?.code;
            const paymentErrorCode = checkoutErrorCode
              || error?.cause?.result?.declineCode
              || error?.cause?.result?.stripeCode;
            const modalTitle = checkoutErrorCode
              ? t(['checkoutErrors.headline', 'paymentErrors.headline'])
              : t('paymentErrors.headline');
            const modalMessage = t([`coupon.errors.${paymentErrorCode}`, `paymentErrors.${paymentErrorCode}`, 'paymentErrors.general']);
            await confirm({
              title: modalTitle,
              description: modalMessage,
              hideCancelButton: true,
            });
            return;
          } finally {
            setStripeLoading(false);
          }
        }
        if (submitError) {
          setStripeLoading(false);
          return;
        }

        const { error, confirmationToken: token } = await stripe.createConfirmationToken({
          elements: stripeElements,
          return_url: `${window.location.origin}/payment-complete/${params.slug}/stripe`,
        });
        confirmationToken = token;
        if (error) {
          setStripeLoading(false);
          await confirm({
            title: t('paymentErrors.headline'),
            description: t('paymentErrors.general'),
            hideCancelButton: true,
          });
          return;
        }
      }
      try {
        const { data } = await createPaymentIntent({
          variables: { slug: params.slug, input: { confirmationTokenId: confirmationToken?.id } },
        });

        if (data?.createPaymentIntent?.status === 'requires_action') {
          try {
            const { error: actionError } = await stripe.handleNextAction({
              clientSecret: data?.createPaymentIntent?.clientSecret,
            });

            if (actionError) {
              const paymentErrorCode = actionError?.decline_code
                || actionError?.declineCode
                || actionError?.code;
              const modalMessage = t([`paymentErrors.${paymentErrorCode}`, 'paymentErrors.general']);
              await confirm({
                title: t('paymentErrors.headline'),
                description: modalMessage,
                hideCancelButton: true,
              });
              setStripeLoading(false);
              return;
            }
          } catch (error) {
            setStripeLoading(false);
            const paymentErrorCode = error?.decline_code
              || error?.declineCode
              || error?.code
              || error?.cause?.result?.declineCode
              || error?.cause?.result?.stripeCode;
            const modalMessage = t([`paymentErrors.${paymentErrorCode}`, 'paymentErrors.general']);
            await confirm({
              title: t('paymentErrors.headline'),
              description: modalMessage,
              hideCancelButton: true,
            });
            return;
          }

          window.location.href = `${window.location.origin}/payment-complete/${params.slug}/stripe`;
          return;
        }

        window.location.href = `${window.location.origin}/payment-complete/${params.slug}/stripe`;
        return;
      } catch (error) {
        setStripeLoading(false);
        if (error?.cause?.result?.code === 'InsufficientInventory') {
          const { data } = await refetch();
          const availabilities = data?.getAvailabilities?.availabilities;
          await confirm({
            title: t('error'),
            description: (
              <>
                <Typography gutterBottom>{t('insufficientInventory')}</Typography>
                <Typography sx={{ marginTop: 1 }}>{t('insufficientInventoryAffectdCategories')}</Typography>
                <ul>
                  {error?.cause?.result?.unavailableProducts?.map((productId) => (
                    <li key={productId}>
                      {details?.products?.find(({ id }) => productId === id)?.name}
                    </li>
                  ))}
                </ul>
              </>
            ),
            hideCancelButton: true,
          });
          for (const productId of error.cause.result.unavailableProducts) {
            const avail = availabilities.find((row) => row.productId === productId);
            formRef.current.change(`product_${productId}`, Math.min(avail.maxQuantity, values[`product_${productId}`]));
          }
          navigate(`/${params.slug}/checkout#step-0`);
          await updateCheckout({
            variables: { slug: params.slug, input: formatStateToCheckout(formRef.current.getState().values) },
          });
        } else {
          const checkoutErrorCode = error?.networkError?.result?.code;
          const paymentErrorCode = checkoutErrorCode
            || error?.cause?.result?.declineCode
            || error?.cause?.result?.stripeCode;
          const modalTitle = checkoutErrorCode
            ? t(['checkoutErrors.headline', 'paymentErrors.headline'])
            : t('paymentErrors.headline');
          const modalMessage = t([`coupon.errors.${paymentErrorCode}`, `paymentErrors.${paymentErrorCode}`, 'paymentErrors.general']);
          await confirm({
            title: modalTitle,
            description: modalMessage,
            hideCancelButton: true,
          });
        }
        setStripeLoading(false);
      }
      return;
    }

    if (steps.length !== (activeStep - 1)) {
      await updateCheckout({
        variables: { slug: params.slug, input: formatStateToCheckout(values) },
      });

      await refetch();

      if (activeStep < steps.length - 1) navigate(`/${params.slug}/checkout#step-${activeStep + 1}`);
    }
  };

  const renderContent = (values) => {
    if (loadingUpdateCheckout || loading) {
      return (
        <Grid sx={{ width: '100%', textAlign: 'center', minHeight: '200px' }}>
          <CircularProgress />
        </Grid>
      );
    }

    if (activeStep === 0) {
      return (<ChooseTicketCategory details={details} values={values} setNextButtonActive={setNextButtonActive} />);
    }
    if (activeStep === 1) {
      return (<AttendeeDetails details={details} values={values} setNextButtonActive={setNextButtonActive} />);
    }
    if (activeStep === 2) {
      return (<InvoiceAddress details={details} values={values} />);
    }

    if (activeStep === 3) {
      if (data.getCheckout.total > 0) {
        const paymentMethodTypes = Object.entries(details.event.paymentMethods)
          .reduce((acc, [paymentMethod, value]) => {
            if (!value.enabled) return acc;
            if (paymentMethod === 'invoicePurchase') return acc;
            if (['google_pay', 'apple_pay'].includes(paymentMethod)) return acc;
            if (!value.productIds?.length) {
              acc.push(paymentMethod);
            } else if (value.productIds.some((productId) => values[`product_${productId}`])) {
              acc.push(paymentMethod);
            }
            return acc;
          }, []);

        const invoicePurchaseMethod = details.event.paymentMethods.invoicePurchase;
        let showInvoicePurchase = false;
        if (invoicePurchaseMethod?.enabled) {
          if (!invoicePurchaseMethod.productIds?.length) {
            showInvoicePurchase = true;
          } else {
            showInvoicePurchase = !!invoicePurchaseMethod.productIds.find((productId) => values[`product_${productId}`]);
          }
        }

        const customPaymentMethods = [];

        if (showInvoicePurchase) {
          customPaymentMethods.push({
            id: details.event.stripeSettings.customPaymentMethodInvoice,
            options: {
              type: 'static',
              subtitle: 'Erhalten Sie eine Rechnung per E-Mail und bezahlen Sie innerhalb von 30 Tagen per Überweisung.',
              embedded: {
                handleRender: (container) => {
                  container.innerHTML = `
                      <div style="padding:12px 0;font-size:14px;line-height:1.5;color:#30313d;">
                        Erhalten Sie eine <strong>Rechnung per E-Mail</strong> und bezahlen Sie innerhalb von
                        <strong>30 Tagen per Überweisung</strong> – keine Kreditkarte erforderlich.
                      </div>
                    `;
                },
                handleDestroy: () => {},
              },
            },
          });
        }

        if (details.event.stripeSettings?.customPaymentMethodTest) {
          customPaymentMethods.push({
            id: details.event.stripeSettings.customPaymentMethodTest,
            options: {
              type: 'static',
              subtitle: 'Testzahlung.',
              embedded: {
                handleRender: (container) => {
                  container.innerHTML = `
                      <div style="padding:12px 0;font-size:14px;line-height:1.5;color:#30313d;">
                        Testzahlung — kein Geld wird belastet. Verwenden Sie diese Methode nur zum Testen.
                      </div>
                    `;
                },
                handleDestroy: () => {},
              },
            },
          });
        }

        const paymentMethodOrder = [
          ...paymentMethodTypes.map((key) => ({
            id: key,
            sequenceId: details.event.paymentMethods[key].sequenceId ?? Infinity,
          })),
          ...(showInvoicePurchase ? [{
            id: details.event.stripeSettings.customPaymentMethodInvoice,
            sequenceId: invoicePurchaseMethod.sequenceId ?? Infinity,
          }] : []),
          ...(details.event.stripeSettings?.customPaymentMethodTest ? [{
            id: details.event.stripeSettings.customPaymentMethodTest,
            sequenceId: Infinity,
          }] : []),
        ]
          .sort((a, b) => a.sequenceId - b.sequenceId)
          .map(({ id }) => id);

        const options = {
          mode: 'payment',
          amount: Math.round(data.getCheckout.total * 100),
          currency: 'eur',
          paymentMethodCreation: 'manual',
          payment_method_types: paymentMethodTypes,
          onBehalfOf: details.event.stripeSettings.stripeConnectAccountId,
          appearance: {
            variables: {
              colorPrimary: details?.event?.colors?.primary || '#1976d2',
            },
            theme: 'stripe',
          },
          customPaymentMethods: customPaymentMethods.length ? customPaymentMethods : undefined,
        };
        return (
          <Elements options={options} stripe={stripePromise}>
            <Review
              checkout={data?.getCheckout}
              details={details}
              values={values}
              setStripe={setStripe}
              setStripeElements={setStripeElements}
              refetchParentCheckout={refetch}
              paymentMethodOrder={paymentMethodOrder}
            />
          </Elements>
        );
      }
      return (
        <Review
          checkout={data?.getCheckout}
          details={details}
          values={values}
          setStripe={setStripe}
          setStripeElements={setStripeElements}
          refetchParentCheckout={refetch}
        />
      );
    }
    return null;
  };

  return (
    <FinalForm
      initialValues={initValues}
      onSubmit={onSubmit}
    >
      {({ handleSubmit, values, form }) => {
        useEffect(() => {
          formRef.current = form;
        }, [form]);
        return (
          <form onSubmit={handleSubmit}>
            <Helmet>
              {pageTitle ? <title>{pageTitle}</title> : null}
              {faviconUrl ? <link rel="icon" href={faviconUrl} /> : null}
            </Helmet>
            <Tracking details={details} />
            <EventDetails details={details} />
            <Box
              sx={{
                ml: 10,
                p: { xs: 2, md: 4 },
                maxWidth: 800,
                mx: 'auto',
                bgcolor: 'background.paper',
                borderRadius: 2,
                boxShadow: 3,
              }}
              ref={contentRef}
            >
              <Stepper activeStep={activeStep} sx={{ mb: 4, marginX: { xs: -2, md: 0 } }} alternativeLabel>
                {steps.map((label) => (
                  <Step key={label} sx={{ maxWidth: { xs: '100px', md: 'unset' }, hyphens: { xs: 'auto', md: 'unset' } }}>
                    <StepLabel>{label}</StepLabel>
                  </Step>
                ))}
              </Stepper>
              {renderContent(values)}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
                {activeStep !== 0 ? (
                  <Button
                    disabled={activeStep === 0 || stripeLoading || loadingUpdateCheckout}
                    onClick={handleBack}
                    variant="outlined"
                  >
                    {t('back')}
                  </Button>
                ) : (<div />)}
                {activeStep === steps.length - 1
                  ? (stripeLoading) ? (
                    <LoadingButton loading variant="outlined">
                      Submit
                    </LoadingButton>
                  ) : (
                    <Button
                      variant="contained"
                      color="primary"
                      type="submit"
                      disabled={stripeLoading}
                    >
                      {t('submit')}
                    </Button>
                  )
                  : (
                  <Button
                    variant="contained"
                    color="primary"
                    disabled={!nextButtonActive || stripeLoading || loadingUpdateCheckout || loadingPaymentIntent}
                    type="submit"
                  >
                    {t('next')}
                  </Button>
                  )}
              </Box>
            </Box>
          </form>
        );
      }}
    </FinalForm>
  );
}

export default CheckoutPage;
